import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import { SubContentProps } from '../LanguageSetting';
import useCertificateOfEmploymentProps, {
  EmpCertificateRequestData,
} from './@hooks/useCertificateOfEmploymentProps';
import FormProvider, {
  FormProviderProps,
} from '@/components/HookForm/FormProvider';
import Label from '@/components/@IntranetLibrary/Label';
import { FormGroup } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import { RHFTextFieldProps } from '@/components/HookForm/RHFTextField';
import ContentDescription, {
  ContentDivider,
} from '@/components/MenuDrawer/MyMenuDrawer/@components/ContentDescription';
import { Body1, Body2 } from '@/components/@IntranetLibrary/Typography';
import EmptyContent from '@/components/@IntranetLibrary/EmptyContent';
import {
  History,
  HistoryItem,
  TextFieldButton,
  TextFieldWithButton,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/CertificateOfEmployment/style';
import RHFTextArea from '@/components/HookForm/RHFTextarea';
import { CertificateRequestForm } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/CertificateOfEmployment/@hooks/useCertificateRequestForm';
import { useMypageEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { MypageEventName } from '@/constant/gtm';
import { useTranslation } from 'react-i18next';

export type CertificateOfEmploymentProps = {
  readonly formProps: FormProviderProps<CertificateRequestForm>;
  readonly isLoading: boolean;
  readonly isCodeRequestLoading: boolean;
  readonly isVerifyLoading: boolean;
  readonly isPrintLoading: boolean;

  readonly isCodeRequested: boolean;
  readonly isVerified: boolean;

  readonly requestCodeButtonTitle: string;
  readonly requestButtonOnClick: () => void;
  readonly isRequestButtonDisabled: boolean;
  readonly codeHelperText?: string;
  readonly codeOnChange: RHFTextFieldProps['onChange'];
  readonly verifyButtonOnClick: () => void;
  readonly isVerifyButtonDisabled: boolean;
  readonly isSubmitButtonDisabled: boolean;
  readonly isRequestHistoryLoading: boolean;
  readonly requestHistory: EmpCertificateRequestData[];
};

const CertificateOfEmployment = (props: SubContentProps) => {
  const { t } = useTranslation();
  const {
    formProps,
    requestCodeButtonTitle,
    requestButtonOnClick,
    isRequestButtonDisabled,
    isLoading,
    isCodeRequestLoading,
    isVerifyLoading,
    isPrintLoading,
    isCodeRequested,
    isVerified,
    codeOnChange,
    codeHelperText,
    verifyButtonOnClick,
    isVerifyButtonDisabled,
    isSubmitButtonDisabled,
    isRequestHistoryLoading,
    requestHistory,
  } = useCertificateOfEmploymentProps(props);
  const mypageEventTracker = useMypageEventTracker();

  return (
    <ContentDivider>
      <ContentDescription hasVerticalMargin={true}>
        {t('MYPAGE.문자 인증을 통해 직접 재직증명서를 발급할 수 있습니다.')}
      </ContentDescription>
      <FormProvider {...formProps}>
        <Stack sx={{ padding: '24px 0 32px', gap: '24px' }}>
          <FormGroup gapsize={'s'}>
            <Label isRequired={true} title={t('휴대폰 번호')} size={14} />
            <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
              <TextFieldWithButton
                name={'phoneNumber'}
                variant={'outlined'}
                disabled={true}
                helperText={t(
                  'MYPAGE.휴대폰 번호가 변경된 경우 HR팀에 문의해 주세요.',
                )}
              />
              <TextFieldButton
                onClick={requestButtonOnClick}
                disabled={isRequestButtonDisabled || isLoading}
                loading={isCodeRequestLoading}
              >
                {requestCodeButtonTitle}
              </TextFieldButton>
            </Box>
          </FormGroup>
          {isCodeRequested && (
            <FormGroup gapsize={'s'}>
              <Label isRequired={true} title={t('인증번호')} size={14} />
              <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
                <TextFieldWithButton
                  name={'verificationCode'}
                  helperText={
                    codeHelperText && (
                      <Typography sx={{ color: '#43B16F', fontSize: '12px' }}>
                        {codeHelperText}
                      </Typography>
                    )
                  }
                  disabled={isLoading || isVerified}
                  variant={'outlined'}
                  placeholder={t('인증번호 {{n}}자리', { n: 6 })}
                  onChange={codeOnChange}
                />
                <TextFieldButton
                  onClick={verifyButtonOnClick}
                  disabled={isVerifyButtonDisabled || isLoading}
                  loading={isVerifyLoading}
                >
                  {t('인증')}
                </TextFieldButton>
              </Box>
            </FormGroup>
          )}
          {isVerified && (
            <>
              <FormGroup gapsize={'s'}>
                <Label
                  isRequired={true}
                  title={t('MYPAGE.사용 용도')}
                  size={14}
                />
                <RHFTextArea
                  name={'purpose'}
                  placeholder={t('{{n}}자 이내로 작성해주세요.', { n: 50 })}
                  minRows={1}
                  maxLength={50}
                />
              </FormGroup>
              <Button
                variant={'contained'}
                size={'large'}
                type={'submit'}
                disabled={isSubmitButtonDisabled || isLoading}
                loading={isPrintLoading}
                fullWidth
                onClick={() => {
                  // GA Mypage - 27번
                  mypageEventTracker(MypageEventName.clickEvent, {
                    area_name: '재직증명서 발급',
                    contents: '발급',
                    status: undefined,
                    description: '재직증명서 발급',
                  });
                }}
              >
                {t('발급')}
              </Button>
            </>
          )}
        </Stack>
      </FormProvider>
      <Stack sx={{ marginTop: '24px' }}>
        <Body1>{t('MYPAGE.증명서 발급이력')}</Body1>
        {isRequestHistoryLoading ? (
          <Stack gap={'12px'} padding={'24px 0'}>
            {[1, 2, 3, 4, 5].map((item) => (
              <Skeleton key={item} variant={'rect'} height={60} />
            ))}
          </Stack>
        ) : requestHistory.length > 0 ? (
          <Stack gap={'16px'} marginTop={'16px'}>
            {requestHistory.map(({ id, requestDate, purpose = '-' }) => (
              <History key={id} sx={{ gap: '8px' }}>
                {[
                  { title: t('MYPAGE.발급일'), content: requestDate },
                  { title: t('MYPAGE.발급 번호'), content: id },
                  { title: t('MYPAGE.사용 용도'), content: purpose },
                ].map(({ title, content }, index) => (
                  <HistoryItem key={`${id}${index}`}>
                    <Body2>{title}</Body2>
                    <Body2 sx={{ color: 'var(--dk-text, #000000)' }}>{content}</Body2>
                  </HistoryItem>
                ))}
              </History>
            ))}
          </Stack>
        ) : (
          <Box sx={{ marginTop: '28px' }}>
            <EmptyContent
              title={t('MYPAGE.발급 이력이 없습니다.')}
              type={'empty'}
            />
          </Box>
        )}
      </Stack>
    </ContentDivider>
  );
};

export default CertificateOfEmployment;
